/**
 * FANPLAZA Wishlist System
 * localStorage-based, works for guests and logged-in users
 * Features: toggle, sync UI, toast feedback, cross-tab sync, mutation observer
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'fp_wishlist';
  var OLD_KEY = 'rpt_wishlist';
  var EVENT_CHANGED = 'fanplaza:wishlist:changed';
  var MAX_ITEMS = 50;

  class FanPlazaWishlist {
    constructor() {
      this._items = this._load();
      this._bindEvents();
      this._syncUI();
    }

    /* ── Storage ── */

    _load() {
      try {
        /* Migrate from old key if needed */
        var raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) {
          var old = localStorage.getItem(OLD_KEY);
          if (old) {
            localStorage.setItem(STORAGE_KEY, old);
            localStorage.removeItem(OLD_KEY);
            raw = old;
          }
        }
        return raw ? JSON.parse(raw) : [];
      } catch (e) {
        return [];
      }
    }

    _save() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this._items));
      } catch (e) { /* storage full */ }
      document.dispatchEvent(new CustomEvent(EVENT_CHANGED, { detail: { items: this._items } }));
      this._syncUI();
    }

    /* ── Public API ── */

    add(handle) {
      if (!handle || this.has(handle)) return;
      this._items.push(handle);
      if (this._items.length > MAX_ITEMS) this._items.shift();
      this._save();
      this._showToast('Added to Wishlist');
    }

    remove(handle) {
      var idx = this._items.indexOf(handle);
      if (idx === -1) return;
      this._items.splice(idx, 1);
      this._save();
      this._showToast('Removed from Wishlist');
    }

    toggle(handle) {
      this.has(handle) ? this.remove(handle) : this.add(handle);
    }

    has(handle) {
      return this._items.indexOf(handle) !== -1;
    }

    getAll() {
      return this._items.slice();
    }

    count() {
      return this._items.length;
    }

    /* ── UI Sync ── */

    _syncUI() {
      var self = this;

      /* Update all bookmark buttons */
      document.querySelectorAll('[data-fp-bookmark]').forEach(function (btn) {
        var handle = btn.getAttribute('data-product-handle');
        if (!handle) return;
        var saved = self.has(handle);
        btn.classList.toggle('is-saved', saved);
        btn.setAttribute('aria-pressed', saved ? 'true' : 'false');

        /* Update label text if present */
        var label = btn.querySelector('.fp-bookmark__label');
        if (label) label.textContent = saved ? 'Saved' : 'Save to Wishlist';
      });

      /* Update header counters */
      var c = self.count();
      document.querySelectorAll('[data-fp-wishlist-count]').forEach(function (el) {
        el.textContent = c > 0 ? c : '';
        el.style.display = c > 0 ? '' : 'none';
      });
    }

    /* ── Toast Notification ── */

    _showToast(message) {
      var existing = document.querySelector('.fp-toast');
      if (existing) existing.remove();

      var toast = document.createElement('div');
      toast.className = 'fp-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      toast.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>' +
        '<span>' + message + '</span>';
      document.body.appendChild(toast);

      requestAnimationFrame(function () {
        toast.classList.add('is-visible');
      });

      setTimeout(function () {
        toast.classList.remove('is-visible');
        setTimeout(function () { toast.remove(); }, 350);
      }, 2500);
    }

    /* ── Event Binding ── */

    _bindEvents() {
      var self = this;

      /* Click delegation for all bookmark buttons */
      document.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-fp-bookmark]');
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();
        var handle = btn.getAttribute('data-product-handle');
        if (handle) self.toggle(handle);
      });

      /* Cross-tab sync via storage event */
      window.addEventListener('storage', function (e) {
        if (e.key === STORAGE_KEY) {
          self._items = self._load();
          self._syncUI();
        }
      });

      /* Re-sync after AJAX / section rendering — debounced */
      var syncTimer = null;
      var observer = new MutationObserver(function () {
        if (syncTimer) return;
        syncTimer = setTimeout(function () {
          syncTimer = null;
          self._syncUI();
        }, 300);
      });
      observer.observe(document.body, { childList: true });
    }
  }

  /* Initialise */
  function init() {
    window.FanPlazaWishlist = new FanPlazaWishlist();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
